import * as XLSXStyle from "xlsx-js-style";
import { ExclusionType, InhabilitadoCargaResult } from "../../../services/inhabilitadosNominaService";

// Helpers compartidos por la carga de inhabilitados (por nómina Excel y
// manual). Son puros (sin JSX): normalización de texto, nivel de exclusión y
// la exportación del resultado a Excel con formato.

export const CLIENT_LEVEL = "cliente";

export const isClientLevelType = (
  excTipoId: string,
  typesById: Map<string, ExclusionType>
): boolean => {
  const tipo = typesById.get(excTipoId);
  return (tipo?.exclusionLevel ?? "").toLowerCase() === CLIENT_LEVEL;
};

/** Nombres: mayúsculas, sin espacios en los extremos ni dobles internos. */
export const toUpperName = (value: string): string =>
  value.trim().replace(/\s+/g, " ").toUpperCase();

/** Comentarios: mayúsculas y sin espacios en los extremos. */
export const toUpperComment = (value: string): string => value.trim().toUpperCase();

// RUT normalizado (sin puntos, guiones ni espacios, en mayúscula) para deduplicar.
export const normalizeRutKey = (value: string): string =>
  value.replace(/[.\-\s]/g, "").toUpperCase();

/**
 * Convierte el texto pegado en la carga manual en filas. Cada línea es un
 * registro; dentro de la línea se acepta separar RUT, nombres y apellidos por
 * coma, punto y coma o tabulación. Si la línea trae solo espacios, el primer
 * token es el RUT y el resto el nombre. Solo el RUT es obligatorio en este paso.
 */
export const parsePastedRows = (
  text: string
): { rut: string; nombres: string; apellidos: string }[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[;,\t]/).map((part) => part.trim());
      const [rut = "", nombres = "", ...rest] = parts;
      if (parts.length === 1) {
        const bySpace = line.split(/\s+/);
        if (bySpace.length > 1) {
          return {
            rut: bySpace[0],
            nombres: bySpace.slice(1).join(" "),
            apellidos: "",
          };
        }
      }
      return { rut, nombres, apellidos: rest.join(" ") || "" };
    })
    .filter((row) => row.rut !== "");

// --- Estilos del Excel de resultados (xlsx-js-style) ------------------------
const XLSX_BORDER = {
  top: { style: "thin", color: { rgb: "D9E2EC" } },
  bottom: { style: "thin", color: { rgb: "D9E2EC" } },
  left: { style: "thin", color: { rgb: "D9E2EC" } },
  right: { style: "thin", color: { rgb: "D9E2EC" } },
};

const XLSX_HEADER_STYLE = {
  font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "14396E" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: XLSX_BORDER,
};

const XLSX_CELL_STYLE = {
  alignment: { vertical: "center", wrapText: true },
  border: XLSX_BORDER,
};

const XLSX_ESTADO_STYLE: Record<string, { bg: string; fg: string }> = {
  CREADO: { bg: "C6EFCE", fg: "166534" },
  DUPLICADO: { bg: "E2E8F0", fg: "334155" },
  RUT_INVALIDO: { bg: "F4CCCC", fg: "991B1B" },
  TIPO_INVALIDO: { bg: "F4CCCC", fg: "991B1B" },
  CLIENTE_INVALIDO: { bg: "F4CCCC", fg: "991B1B" },
  CLIENTE_REQUERIDO: { bg: "FCE5CD", fg: "9A3412" },
  COMENTARIO_REQUERIDO: { bg: "FCE5CD", fg: "9A3412" },
  ERROR: { bg: "FCE5CD", fg: "9A3412" },
};

const styleResultSheet = (
  sheet: XLSXStyle.WorkSheet,
  options: { widths: number[]; estadoCol?: number; boldFirstCol?: boolean }
): void => {
  const ref = sheet["!ref"];
  if (!ref) return;
  const range = XLSXStyle.utils.decode_range(ref);
  sheet["!cols"] = options.widths.map((wch) => ({ wch }));
  sheet["!rows"] = [{ hpt: 22 }];

  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSXStyle.utils.encode_cell({ r: R, c: C });
      const cell = (sheet as any)[addr];
      if (!cell) continue;

      if (R === 0) {
        cell.s = XLSX_HEADER_STYLE;
        continue;
      }

      const zebra = R % 2 === 1 ? { fgColor: { rgb: "F5F8FC" } } : undefined;
      cell.s = { ...XLSX_CELL_STYLE, ...(zebra ? { fill: zebra } : {}) };

      if (options.boldFirstCol && C === 0) {
        cell.s = { ...cell.s, font: { bold: true, color: { rgb: "334155" } } };
      }

      if (options.estadoCol !== undefined && C === options.estadoCol) {
        const estado = String(cell.v ?? "").toUpperCase();
        const tone = XLSX_ESTADO_STYLE[estado];
        if (tone) {
          cell.s = {
            border: XLSX_BORDER,
            font: { bold: true, color: { rgb: tone.fg } },
            fill: { fgColor: { rgb: tone.bg } },
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      }
    }
  }
};

/** Exporta el resultado a Excel: hoja "Detalle" (con estados) + "Resumen". */
export const downloadInhabilitadosResult = (
  result: InhabilitadoCargaResult,
  baseName: string
): void => {
  const resumen = result.resumen;

  const detalleData = result.detalle.map((item) => ({
    RUT: item.rut,
    "RUT normalizado": item.rutNormalizado,
    Nombre: item.nombre,
    Tipo: item.tipo,
    Cliente: item.cliente,
    Comentario: item.comentario,
    Estado: item.estado,
    Detalle: item.mensaje,
    "Persona creada": item.personaCreada ? "Sí" : "No",
  }));
  const detalleSheet = XLSXStyle.utils.json_to_sheet(detalleData);
  styleResultSheet(detalleSheet, {
    widths: [14, 16, 26, 22, 20, 34, 20, 42, 14],
    estadoCol: 6,
  });

  const resumenData = [
    { Métrica: "Total registros", Valor: resumen.totalRegistros },
    { Métrica: "Inhabilitados (creados)", Valor: resumen.creados },
    { Métrica: "Personas creadas", Valor: resumen.personasCreadas },
    { Métrica: "Personas vinculadas", Valor: resumen.personasVinculadas },
    { Métrica: "Duplicados", Valor: resumen.duplicados },
    { Métrica: "RUT inválido", Valor: resumen.rutInvalidos },
    { Métrica: "Tipo inválido", Valor: resumen.tipoInvalidos },
    { Métrica: "Cliente requerido", Valor: resumen.clienteRequerido },
    { Métrica: "Cliente inválido", Valor: resumen.clienteInvalidos },
    { Métrica: "Comentario requerido", Valor: resumen.comentarioRequerido },
    { Métrica: "Errores", Valor: resumen.errores },
  ];
  const resumenSheet = XLSXStyle.utils.json_to_sheet(resumenData);
  styleResultSheet(resumenSheet, { widths: [30, 12], boldFirstCol: true });

  const workbook = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(workbook, detalleSheet, "Detalle");
  XLSXStyle.utils.book_append_sheet(workbook, resumenSheet, "Resumen");

  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  XLSXStyle.writeFile(workbook, `resultado_${baseName}_${stamp}.xlsx`);
};
